import timezones from 'timezones-list';
import React from 'react';

import {
  type FormAutocompleteProps,
  FormAutocomplete,
} from '../FormAutocomplete';

export type FormTimezoneProps = Omit<
  FormAutocompleteProps,
  'options' | 'total'
> & {
  /** Keeps only the zones this predicate accepts (organization settings pass
   *  `isIanaTimeZone`, because the API validates with it). */
  filterOption?: (tzCode: string) => boolean;
  /** Kept in the list even when the package does not carry it, so a stored
   *  value can always be displayed. */
  currentValue?: null | string;
};
export const FormTimezone = ({
  currentValue,
  filterOption,
  name,
  ...props
}: FormTimezoneProps) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const allOptions = React.useMemo(() => {
    const listed = timezones
      .filter((tz) => !filterOption || filterOption(tz.tzCode))
      .map((tz) => {
        return {
          label: tz.label,
          value: tz.tzCode,
        };
      });
    const keepCurrent =
      !!currentValue &&
      (!filterOption || filterOption(currentValue)) &&
      !listed.some((tz) => tz.value === currentValue);
    return keepCurrent
      ? [...listed, { label: currentValue, value: currentValue }]
      : listed;
  }, [currentValue, filterOption]);
  const options = React.useMemo(() => {
    return allOptions.filter((tz) =>
      searchTerm
        ? tz.label.toLowerCase().includes(searchTerm.toLowerCase())
        : true,
    );
  }, [allOptions, searchTerm]);

  return (
    <FormAutocomplete
      {...props}
      onSearch={async (term) => setSearchTerm(term)}
      total={options.length}
      options={options}
      name={name}
    />
  );
};

export default FormTimezone;
